import React, { MutableRefObject, Ref, RefCallback } from "react";

/**
 * JS `%` is signed — `(-1) % 5` returns `-1`, not `4`.
 * This helper always returns a non-negative remainder, which is required for
 * the circular index arithmetic in `circularOffset`.
 */
const positiveMod = (n: number, m: number) => ((n % m) + m) % m;

/**
 * Maps a linear item `index` to a circular slot offset enabling seamless infinite looping.
 *
 * Because `activeIndex` in `RotationalCarousel` is unbounded (never clamped),
 * this function re-maps any integer distance into the shortest circular path
 * around the list. For example, with 5 items and `activeIndex = 4`:
 *   - item 0 → offset +1  (one step clockwise from active)
 *   - item 3 → offset -1  (one step counter-clockwise)
 *
 * @param index - The item's static zero-based index in the list.
 * @param activeIndex - The carousel's current (unbounded) active index.
 * @param total - Total number of items in the carousel.
 * @returns A signed slot offset; 0 = center, negative = left, positive = right.
 */
const circularOffset = (
  index: number,
  activeIndex: number,
  total: number,
): number => {
  // Before items register their count, total is 0 — fall back to a simple
  // linear difference so the center card still renders correctly.
  if (total === 0) return index - activeIndex;
  const raw = index - activeIndex;
  const half = Math.floor(total / 2);
  return positiveMod(raw + half, total) - half;
};

/**
 * Returns the CSS `transform`, `zIndex`, and `opacity` values for a carousel
 * card at the given slot `offset`.
 *
 * All cards share `left: 50%` in the DOM. `translateX` shifts each card so
 * that its visual center lands at the intended position:
 * ```
 *   visual_center = 50% + translateX + halfWidth
 *   =>  translateX = target_offset_px - halfWidth
 * ```
 *
 * Slot assignments:
 * - `offset  0` — center card, full size, fully opaque, highest z-index.
 * - `offset ±1` — visible side cards, scaled down, fully opaque.
 * - `offset ±2` — preloaded buffer cards, pushed far off-screen,
 *   invisible (`opacity: 0`, `pointerEvents: none`), ready to animate in
 *   when the user navigates past the current side card.
 *
 * @param offset - Circular slot offset from `circularOffset`.
 * @param halfWidth - Half the center card's rendered pixel width.
 * @param sideScale - CSS scale factor for side cards (e.g. 0.8).
 * @param sideOffset - Pixel distance from center to each side card's visual center.
 */
const slotStyle = (
  offset: number,
  halfWidth: number,
  sideScale: number,
  sideOffset: number,
): React.CSSProperties => {
  if (offset === 0) {
    return {
      transform: `translateX(${-halfWidth}px) scale(1)`,
      zIndex: 10,
      opacity: 1,
    };
  }
  if (offset === -1) {
    return {
      transform: `translateX(${-sideOffset - halfWidth}px) scale(${sideScale})`,
      zIndex: 5,
      opacity: 0.7,
    };
  }
  if (offset === 1) {
    return {
      transform: `translateX(${sideOffset - halfWidth}px) scale(${sideScale})`,
      zIndex: 5,
      opacity: 0.7,
    };
  }

  // Offset ±2: preloaded off-screen. Pushed to ~2.8× sideOffset so they are
  // well outside the visible area but close enough to animate smoothly into
  // the ±1 slot without traversing the full viewport width.
  const sign = offset < 0 ? -1 : 1;
  return {
    transform: `translateX(${sign * sideOffset * 2.8 - halfWidth}px) scale(${sideScale * 0.75})`,
    zIndex: 1,
    opacity: 0,
    pointerEvents: "none",
  };
};

/**
 * Combines multiple React refs (object refs or callback refs) into a single
 * callback ref that forwards the DOM node to all of them.
 *
 * Used in `RotationalCarouselItem` to attach both the consumer's forwarded ref
 * and the internal `measureRef` to the same DOM node, so outside callers can
 * still access the element while the component also reads its dimensions.
 */
const mergeRefs =
  <T>(...refs: (Ref<T> | undefined)[]): RefCallback<T> =>
  (node) =>
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === "function") ref(node);
      else (ref as MutableRefObject<T | null>).current = node;
    });

export { positiveMod, circularOffset, slotStyle, mergeRefs };
